import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { lowerCase } from "lodash";
import { useCallback, useMemo } from "react";
import { useFragment } from "react-relay";

import { useFacilityLessonNoun } from "@/features/Facility";
import { lessonTypeHasRateForMembershipType } from "@/screens/Lessons/helpers/lessonTypeHasRateForMembershipType";
import { DropdownSelect } from "@whoosh/design-system/src/components/controls";
import { VStack, Text } from "@whoosh/design-system/src/components/primitive";

import { LessonTypeFilterOption_lessonType$key } from "./__generated__/LessonTypeFilterOption_lessonType.graphql";
import { LessonTypeFilter_facility$key } from "./__generated__/LessonTypeFilter_facility.graphql";
import { LessonTypeFilter_session$key } from "./__generated__/LessonTypeFilter_session.graphql";

type Props = {
  sessionRef: LessonTypeFilter_session$key | null;
  facilityRef: LessonTypeFilter_facility$key | null;
  onChange: (lessonTypeId: string, defaultDurationMins: number) => void;
  selectedLessonTypeId?: string;
};

export const LessonTypeFilter = ({
  sessionRef,
  facilityRef,
  onChange,
  selectedLessonTypeId,
}: Props) => {
  const facility = useFragment(LessonTypeFilterFragmentString, facilityRef);
  const session = useFragment(
    LessonTypeFilterSessionFragmentString,
    sessionRef
  );

  const membershipTypeId =
    session?.currentClientProfile?.member?.membershipType?.id || "";

  const { singular: lessonNoun } = useFacilityLessonNoun(facility ?? null);

  const validLessonTypes = useMemo(() => {
    return facility?.lessonTypes?.edges?.flatMap((e) =>
      e?.node &&
      e.node.visibleToMembers &&
      lessonTypeHasRateForMembershipType(e.node, membershipTypeId)
        ? [e.node]
        : []
    );
  }, [facility?.lessonTypes?.edges, membershipTypeId]);

  const getDefaultDurationFromLessonTypeId = useCallback(
    (lessonTypeId: string) => {
      return validLessonTypes?.find((e) => e.id === lessonTypeId)
        ?.defaultDurationMins;
    },
    [validLessonTypes]
  );

  const lessonTypeOptions =
    validLessonTypes?.flatMap((e) =>
      e &&
      e.visibleToMembers &&
      lessonTypeHasRateForMembershipType(e, membershipTypeId)
        ? [
            {
              value: e.id,
              label: <LessonTypeFilterOption lessonTypeRef={e} />,
              triggerElement: <Text variant="mainBaseSemiBold">{e.name} </Text>,
            },
          ]
        : []
    ) ?? [];

  return (
    <VStack gap={3}>
      <Text variant="mainSmallSemiBold">{t`${lessonNoun} Type`}</Text>
      <DropdownSelect
        hideOuterMargin
        value={selectedLessonTypeId}
        options={lessonTypeOptions}
        onChange={(val) => {
          onChange(val, getDefaultDurationFromLessonTypeId(val) ?? 0);
        }}
        placeholder={t`Any ${lowerCase(lessonNoun)} type`}
      />
    </VStack>
  );
};

type OptionProps = {
  lessonTypeRef: LessonTypeFilterOption_lessonType$key | null;
};
const LessonTypeFilterOption = ({ lessonTypeRef }: OptionProps) => {
  const lessonType = useFragment(
    LessonTypeFilterOptionFragmentString,
    lessonTypeRef
  );

  return (
    <VStack gap={1}>
      <Text variant="mainBaseSemiBold">{lessonType?.name}</Text>
      <Text variant="mainTiny" color="text-secondary">
        {lessonType?.description}
      </Text>
    </VStack>
  );
};

const LessonTypeFilterSessionFragmentString = graphql`
  fragment LessonTypeFilter_session on Session {
    currentClientProfile {
      member {
        membershipType {
          id
        }
      }
    }
  }
`;

const LessonTypeFilterOptionFragmentString = graphql`
  fragment LessonTypeFilterOption_lessonType on LessonType {
    id
    index
    name
    description
    visibleToMembers
  }
`;

const LessonTypeFilterFragmentString = graphql`
  fragment LessonTypeFilter_facility on Facility {
    ...useFacilityLessonNoun_facility
    lessonTypes {
      edges {
        node {
          id
          index
          visibleToMembers
          name
          defaultDurationMins
          ...lessonTypeHasRateForMembershipType_lessonType
          ...LessonTypeFilterOption_lessonType
        }
      }
    }
  }
`;
